import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Martini, Loader2, Link2, Copy, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CocktailSubmissions = ({ token }) => {
  const [submissions, setSubmissions] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attaching, setAttaching] = useState(null);
  const [jobChoice, setJobChoice] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [subs, hook, allJobs] = await Promise.all([
        axios.get(`${API}/admin/cocktail-submissions`, { headers }),
        axios.get(`${API}/admin/cocktail-webhook-url`, { headers }),
        axios.get(`${API}/admin/jobs`, { headers }),
      ]);
      setSubmissions(subs.data.submissions || []);
      setWebhookUrl(hook.data.webhook_url || '');
      setJobs(allJobs.data || []);
    } catch (err) {
      toast.error('Could not load cocktail submissions');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const attach = async (submissionId) => {
    const jobId = jobChoice[submissionId];
    if (!jobId) {
      toast.error('Pick a job first');
      return;
    }
    setAttaching(submissionId);
    try {
      const res = await axios.post(
        `${API}/admin/cocktail-submissions/${submissionId}/attach`,
        { job_id: jobId }, { headers }
      );
      toast.success(res.data.message);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not attach that submission');
    } finally {
      setAttaching(null);
    }
  };

  const needsChecking = submissions.filter((s) => s.status === 'needs_checking');
  const matched = submissions.filter((s) => s.status === 'matched').slice(0, 10);
  const upcoming = [...jobs]
    .filter((j) => (j.date || '') >= new Date().toISOString().slice(0, 10))
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading cocktail submissions…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="cocktail-submissions">
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Link2 className="w-5 h-5 text-gold-deep" /> Website form webhook
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-slate-600">
            Paste this into the <strong>Cocktail choices</strong> form in Elementor
            (Actions After Submit → Webhook → Webhook URL):
          </p>
          <div className="flex gap-2">
            <Input value={webhookUrl} readOnly className="font-mono text-xs" data-testid="cocktail-webhook-url" />
            <Button
              variant="outline"
              onClick={() => { navigator.clipboard?.writeText(webhookUrl); toast.success('Webhook URL copied'); }}
              data-testid="copy-webhook-url"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-slate-400">Keep this private — the long code at the end is the password for the webhook.</p>
        </CardContent>
      </Card>

      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Needs checking ({needsChecking.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {needsChecking.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="no-needs-checking">
              Nothing to check — every submission has been matched to a job.
            </p>
          )}

          {needsChecking.map((submission) => (
            <div key={submission.id} className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid={`submission-${submission.id}`}>
              <div className="text-sm">
                <p className="font-medium text-amber-900">
                  {submission.name || 'Unknown customer'}
                  {submission.booking ? ` · booking ${submission.booking}` : ''}
                </p>
                <p className="text-xs text-amber-700">{submission.email || 'no email given'} · {submission.match_reason}</p>
              </div>

              <div className="flex flex-wrap gap-1">
                {(submission.cocktails || []).map((c, i) => (
                  <Badge key={i} variant="outline" className="text-xs bg-white">{c}</Badge>
                ))}
              </div>
              {submission.offmenu && <p className="text-xs text-amber-800">Off-menu: {submission.offmenu}</p>}
              {submission.notes && <p className="text-xs text-amber-800">Notes: {submission.notes}</p>}

              <div className="flex flex-wrap gap-2 items-center">
                <select
                  className="min-w-[260px]"
                  value={jobChoice[submission.id] || ''}
                  onChange={(e) => setJobChoice({ ...jobChoice, [submission.id]: e.target.value })}
                  data-testid={`attach-select-${submission.id}`}
                >
                  <option value="">Choose the job this belongs to…</option>
                  {upcoming.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.date} · {job.client_name} · {job.job_type}
                      {job.job_number ? ` · order ${job.job_number}` : ''}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  disabled={attaching === submission.id}
                  onClick={() => attach(submission.id)}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid={`attach-btn-${submission.id}`}
                >
                  {attaching === submission.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                  Attach to job
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Martini className="w-5 h-5 text-gold-deep" /> Recent submissions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {matched.length === 0 && <p className="text-sm text-slate-500">No submissions yet.</p>}
          {matched.map((submission) => (
            <div key={submission.id} className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2 last:border-0 text-sm">
              <div>
                <p className="font-medium text-slate-800">{submission.name || submission.email}</p>
                <p className="text-xs text-slate-500">
                  {(submission.cocktails || []).join(', ') || 'no cocktails listed'} · {submission.match_reason}
                </p>
              </div>
              <Badge className="bg-green-100 text-green-800 text-xs flex-shrink-0">matched</Badge>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default CocktailSubmissions;
